import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Server,
  Activity,
  AlertTriangle,
  Clock,
} from 'lucide-react';

interface DashboardData {
  capacity: {
    raw: number;
    usable: number;
    used: number;
    available: number;
    efficiency: number;
  };
  drives: {
    total: number;
    healthy: number;
    warning: number;
    failed: number;
    missing: number;
    rebuilding: number;
  };
  enclosures: number;
  pools: { total: number; healthy: number; degraded: number; faulted: number };
  shares: { smb: number; nfs: number };
  iscsiSessions: number;
  performance: {
    readThroughput: number;
    writeThroughput: number;
    readIops: number;
    writeIops: number;
    latency: number;
  };
  system: { cpu: number; memory: number; networkRx: number; networkTx: number; uptime: string };
  alerts: { severity: string; message: string; time: string }[];
  recentActions: { action: string; user: string; time: string }[];
  failedJobs: { operation: string; error: string; time: string }[];
  charts: {
    throughput: { time: string; read: number; write: number }[];
    iops: { time: string; read: number; write: number }[];
    latency: { time: string; latency: number }[];
  };
}

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(2)} ${units[i]}`;
};

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [range, setRange] = useState('1h');

  useEffect(() => {
    fetch(`/api/v1/dashboard?range=${range}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => setData(res.data))
      .catch(console.error);
  }, [range]);

  if (!data) {
    return <div className="p-8 text-center text-slate-500">Loading dashboard...</div>;
  }

  const statCards = [
    { label: 'Raw Capacity', value: formatBytes(data.capacity.raw), sub: `${formatBytes(data.capacity.used)} used` },
    { label: 'Usable Capacity', value: formatBytes(data.capacity.usable), sub: `${formatBytes(data.capacity.available)} free` },
    { label: 'Physical Drives', value: data.drives.total, sub: `${data.drives.healthy} healthy` },
    { label: 'Storage Pools', value: data.pools.total, sub: `${data.pools.degraded} degraded` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm"
        >
          <option value="1h">Last 1 hour</option>
          <option value="6h">Last 6 hours</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="text-sm text-slate-500 dark:text-slate-400">{card.label}</div>
            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{card.value}</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{card.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold text-slate-900 dark:text-white">Throughput</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.charts.throughput}>
                <defs>
                  <linearGradient id="colorRead" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorWrite" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="time" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none' }} />
                <Legend />
                <Area type="monotone" dataKey="read" stroke="#6366f1" fillOpacity={1} fill="url(#colorRead)" />
                <Area type="monotone" dataKey="write" stroke="#10b981" fillOpacity={1} fill="url(#colorWrite)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <AlertTriangle size={16} /> Active Alerts ({data.alerts.length})
            </h2>
            <ul className="space-y-2">
              {data.alerts.map((alert, idx) => (
                <li key={idx} className="text-sm border-l-2 border-red-500 pl-3 py-1">
                  <div className="font-medium text-slate-800 dark:text-slate-200">{alert.message}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{alert.time}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Clock size={16} /> Recent Actions
            </h2>
            <ul className="space-y-2">
              {data.recentActions.map((action, idx) => (
                <li key={idx} className="text-sm text-slate-700 dark:text-slate-300">
                  <span className="font-medium">{action.action}</span>
                  <span className="text-slate-500 dark:text-slate-400"> — {action.user}</span>
                  <div className="text-xs text-slate-400">{action.time}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Server size={16} /> Enclosures
          </div>
          <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{data.enclosures}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Activity size={16} /> Latency
          </div>
          <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{data.performance.latency.toFixed(1)} ms</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">CPU / Memory</div>
          <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            {data.system.cpu}% / {data.system.memory}%
          </div>
        </div>
      </div>
    </div>
  );
}
